import { useMemo, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Table } from 'barte-design-system';
import type { ColumnDefinition } from 'barte-design-system';

import styles from './operation-result-table.module.css';
import type { FeedbackBadgeProps, OperationResultTableProps, OperationResultTableRow } from './operation-result-table.type';

const FeedbackBadge = ({ record }: FeedbackBadgeProps) => {
  const { t } = useTranslation();
  const [tooltipPos, setTooltipPos] = useState<{ top: number; left: number } | null>(null);
  const badgeRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = () => {
    if (!badgeRef.current) return;
    const rect = badgeRef.current.getBoundingClientRect();
    setTooltipPos({
      top: rect.top - 8,
      left: rect.left + rect.width / 2,
    });
  };

  const handleMouseLeave = () => setTooltipPos(null);

  return (
    <div
      ref={badgeRef}
      className={styles['feedback-tooltip-wrapper']}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div
        className={[
          styles['result-feedback-badge'],
          styles[`result-feedback-badge--${record.success ? 'success' : 'error'}`],
        ].join(' ')}
      >
        <span
          className={[
            styles['feedback-dot'],
            styles[`feedback-dot--${record.success ? 'success' : 'error'}`],
          ].join(' ')}
        />
        {record.success
          ? t('backoffice.terminalsDrawerResultFeedbackSuccess')
          : t('backoffice.terminalsDrawerResultFeedbackError')}
      </div>
      {tooltipPos &&
        createPortal(
          <div
            className={styles['feedback-tooltip-fixed']}
            style={{ top: tooltipPos.top, left: tooltipPos.left }}
          >
            <span className={styles['feedback-tooltip-title']}>{record.tooltipTitle}</span>
            <span className={styles['feedback-tooltip-body']}>{record.tooltipBody}</span>
          </div>,
          document.body,
        )}
    </div>
  );
};

export const OperationResultTable = ({ items, entityColumnLabel, subEntityColumnLabel }: OperationResultTableProps) => {
  const { t } = useTranslation();

  const columns = useMemo((): ColumnDefinition<OperationResultTableRow>[] => [
    {
      key: 'serialNumber',
      title: t('backoffice.terminalsDrawerResultColumnSerial'),
      dataIndex: 'serialNumber',
    },
    {
      key: 'entity',
      title: entityColumnLabel,
      dataIndex: 'entityId',
      render: (_: unknown, record: OperationResultTableRow) =>
        record.entityId != null ? (
          <div className={styles['result-cell-two-line']}>
            <span className={styles['result-cell-id']}>{record.entityId}</span>
            {record.entityName && (
              <span className={styles['result-cell-name']}>{record.entityName}</span>
            )}
          </div>
        ) : (
          <span className={styles['result-empty']}>—</span>
        ),
    },
    {
      key: 'subEntity',
      title: subEntityColumnLabel,
      dataIndex: 'subEntityId',
      render: (_: unknown, record: OperationResultTableRow) =>
        record.subEntityId != null ? (
          <div className={styles['result-cell-two-line']}>
            <span className={styles['result-cell-id']}>{record.subEntityId}</span>
            {record.subEntityName && (
              <span className={styles['result-cell-name']}>{record.subEntityName}</span>
            )}
          </div>
        ) : (
          <span className={styles['result-empty']}>—</span>
        ),
    },
    {
      key: 'feedback',
      title: t('backoffice.terminalsDrawerResultColumnFeedback'),
      dataIndex: 'success',
      render: (_: unknown, record: OperationResultTableRow) => (
        <FeedbackBadge record={record} />
      ),
    },
  ], [t, entityColumnLabel, subEntityColumnLabel]);

  return (
    <div className={styles['result-table-wrapper']}>
      <Table<OperationResultTableRow>
        data={items}
        columns={columns}
        rowKey={(record) => record.serialNumber}
        size="sm"
      />
    </div>
  );
};
